import { type FC, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import type { RestreamDestination } from "@/partner/entities/streams";
import { useUpdateRestreamDestinationMutation } from "@/partner/entities/streams";

interface EditRestreamDestinationDialogProps {
  contentId: string;
  destination: RestreamDestination | null;
  onClose: () => void;
}

// Бэкенд UpdateRestreamDestinationInput принимает только title/enabled —
// url и streamKey показываются disabled (неизменяемы после создания).
export const EditRestreamDestinationDialog: FC<
  EditRestreamDestinationDialogProps
> = ({ contentId, destination, onClose }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  // Референсный EditRestreamDialog.js: диалог занимает весь экран на sm-.
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [title, setTitle] = useState("");
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    setTitle(destination?.title ?? "");
    setEnabled(destination?.enabled ?? false);
    // Диалог переиспользуется между строками таблицы — стейт нужно
    // переинициализировать при смене редактируемого назначения.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination?.id]);

  const [updateDestination, { loading }] = useUpdateRestreamDestinationMutation(
    {
      refetchQueries: ["Stream"],
      onCompleted: onClose,
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    }
  );

  const handleSubmit = () => {
    if (!destination) return;
    void updateDestination({
      variables: {
        contentId,
        id: destination.id,
        input: { title, enabled },
      },
    });
  };

  return (
    <Dialog
      open={Boolean(destination)}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      scroll="body"
    >
      <DialogTitle>{t("streams.form.restream.editDialogTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            fullWidth
            required
            autoFocus
            label={t("streams.form.restream.titleField")}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
          <TextField
            fullWidth
            disabled
            label={t("streams.form.restream.urlField")}
            value={destination?.url ?? ""}
          />
          <TextField
            fullWidth
            disabled
            label={t("streams.form.restream.streamKeyField")}
            value={destination?.streamKey ?? ""}
          />
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(event) => setEnabled(event.target.checked)}
              />
            }
            label={t("streams.form.restream.enabledColumn")}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.actions.cancel")}</Button>
        <Button
          variant="contained"
          disabled={!title.trim() || loading}
          onClick={handleSubmit}
        >
          {t("common.actions.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
